"use client";

import { useCallback, useEffect, useRef, type RefObject } from "react";
import interact from "./interact";
import {
    CANVAS_W,
    buildSnapContext,
    clampTransform,
    snapMove,
    snapResize,
    SNAP_SCREEN_PX,
    toPct,
    toPx,
    type ElementTransform,
    type PxBox,
    type ResizeHandle,
    type SnapContext,
    type SnapGuide,
} from "@/features/lomdaEditorV2/lib/elements";
import { useDocumentActions, useUiActions } from "@/features/lomdaEditorV2/store";

/** Minimal shape we read off an interact.js drag event. */
export interface GestureEvent {
    dx: number;
    dy: number;
    clientX: number;
    clientY: number;
    shiftKey: boolean;
    altKey: boolean;
}

export interface GestureWork {
    /**
     * The RAW, unsnapped box — the true pointer position. `mutate` advances this
     * by the incremental pointer delta each frame. Snapping is computed FROM it
     * but never written back, so dragging past the threshold releases cleanly.
     */
    box: PxBox;
    rect: DOMRect;
    scale: number;
    ctx: SnapContext;
    snapThreshold: number;
}

/** Tells `update` how to snap the freshly-mutated raw box, and which badge to show. */
export type SnapSpec =
    | { kind: "move"; disable: boolean }
    | { kind: "resize"; handle: ResizeHandle; disable: boolean; aspect: boolean }
    | { kind: "rotate"; disable: boolean };

/** Suppress text selection (and clear any active selection) during a drag. */
function setBodySelecting(disabled: boolean) {
    if (typeof document === "undefined") return;
    document.body.style.userSelect = disabled ? "none" : "";
    (
        document.body.style as unknown as { webkitUserSelect: string }
    ).webkitUserSelect = disabled ? "none" : "";
    if (disabled) window.getSelection()?.removeAllRanges();
}

/**
 * Drives a move/resize/rotate gesture for one element. All geometry runs in
 * canvas-px space; the live (snapped) value is written to the UI store so the
 * canvas stays smooth and the whole gesture collapses to ONE undo step on
 * commit. The raw box stays the source of truth; the snapped box is derived for
 * display + commit only.
 */
export function useCanvasGesture(
    sceneId: string,
    elementId: string,
    transform: ElementTransform,
    getCanvasRect: () => DOMRect | null,
    /** Other elements' boxes in canvas-px — the snap targets for this gesture. */
    getSiblingBoxes: () => PxBox[]
) {
    const { updateElementTransform } = useDocumentActions();
    const { beginLiveTransform, setLiveFrame, endLiveTransform } =
        useUiActions();

    const transformRef = useRef(transform);
    transformRef.current = transform;
    const siblingsRef = useRef(getSiblingBoxes);
    siblingsRef.current = getSiblingBoxes;
    const work = useRef<GestureWork | null>(null);
    // The snapped box to actually commit (may differ from the raw `work.box`).
    const commitBox = useRef<PxBox | null>(null);
    const moved = useRef(false);
    // Detaches the window pointer-release safety net for the live gesture.
    const detachSafetyNet = useRef<(() => void) | null>(null);
    // Latest `end`, so the safety-net listener always calls the current one.
    const endRef = useRef<() => void>(() => {});

    const start = useCallback(() => {
        const rect = getCanvasRect();
        if (!rect) return;
        const scale = CANVAS_W / rect.width;
        const box = toPx(transformRef.current);
        work.current = {
            box,
            rect,
            scale,
            ctx: buildSnapContext(siblingsRef.current()),
            snapThreshold: SNAP_SCREEN_PX * scale,
        };
        commitBox.current = box;
        moved.current = false;
        setBodySelecting(true);
        beginLiveTransform(elementId, transformRef.current);

        // Safety net: if interact's own `end` is ever missed (e.g. its draggable
        // is torn down mid-gesture), a window pointer release still finalizes the
        // drag — otherwise the guide overlay can get stranded on screen.
        const onRelease = () => endRef.current();
        window.addEventListener("pointerup", onRelease);
        window.addEventListener("pointercancel", onRelease);
        detachSafetyNet.current = () => {
            window.removeEventListener("pointerup", onRelease);
            window.removeEventListener("pointercancel", onRelease);
        };
    }, [getCanvasRect, beginLiveTransform, elementId]);

    const update = useCallback(
        (mutate: (w: GestureWork) => PxBox, snap?: SnapSpec) => {
            const w = work.current;
            if (!w) return;
            // Advance the raw box by this frame's pointer delta.
            w.box = mutate(w);
            let display = w.box;
            let guides: SnapGuide[] = [];
            if (snap && !snap.disable) {
                if (snap.kind === "move") {
                    ({ box: display, guides } = snapMove(
                        w.box,
                        w.ctx,
                        w.snapThreshold
                    ));
                } else if (snap.kind === "resize") {
                    ({ box: display, guides } = snapResize(
                        w.box,
                        snap.handle,
                        w.ctx,
                        w.snapThreshold,
                        snap.aspect
                    ));
                }
            }
            commitBox.current = display;
            moved.current = true;
            setLiveFrame({
                transform: toPct(display),
                kind: snap?.kind ?? "move",
                guides,
            });
        },
        [setLiveFrame]
    );

    const end = useCallback(() => {
        // Guard against a double fire (interact's `end` + the safety net).
        if (!work.current && !commitBox.current) return;
        detachSafetyNet.current?.();
        detachSafetyNet.current = null;
        // Only commit (one undo step) if the gesture actually moved something.
        if (moved.current && commitBox.current) {
            updateElementTransform(
                sceneId,
                elementId,
                clampTransform(toPct(commitBox.current))
            );
        }
        work.current = null;
        commitBox.current = null;
        moved.current = false;
        setBodySelecting(false);
        endLiveTransform();
    }, [updateElementTransform, endLiveTransform, sceneId, elementId]);
    endRef.current = end;

    // If this element unmounts mid-gesture, drop the safety-net listeners.
    useEffect(() => () => detachSafetyNet.current?.(), []);

    return { start, update, end };
}

/**
 * Attach an interact.js draggable to a node. We never use interact's built-in
 * translation — we only read `dx/dy/client/shift/alt` and drive React state — so
 * the element position stays the single source of truth.
 */
export function useInteractDraggable(
    ref: RefObject<HTMLElement>,
    enabled: boolean,
    onStart: () => void,
    onMove: (e: GestureEvent) => void,
    onEnd: () => void,
    ignoreFrom?: string,
    /** Cursor to show on hover. interact.js forces `move` on every draggable
     *  otherwise, which clobbers resize/rotate handle cursors. */
    cursor = "move"
) {
    const handlers = useRef({ onStart, onMove, onEnd });
    handlers.current = { onStart, onMove, onEnd };
    const cursorRef = useRef(cursor);
    cursorRef.current = cursor;

    useEffect(() => {
        const el = ref.current;
        if (!el || !enabled) return;
        const instance = interact(el).draggable({
            inertia: false,
            ignoreFrom,
            cursorChecker: () => cursorRef.current,
            listeners: {
                start: () => handlers.current.onStart(),
                move: (e) => handlers.current.onMove(e as unknown as GestureEvent),
                end: () => handlers.current.onEnd(),
            },
        });
        return () => {
            instance.unset();
        };
    }, [ref, enabled, ignoreFrom]);
}
